/**
 * As cores do mapa vivem no SCSS (custom properties em `.eclipse-app`) e são lidas
 * daqui com `getComputedStyle`.
 *
 * Porquê: o Leaflet recebe as cores como *opções de JS* (`fillColor`, `color`), não
 * por CSS — logo alguém tem de as ter em JS. Ter a rampa duplicada em SCSS e em TS
 * seria garantia de divergirem no primeiro ajuste de design; ler as custom properties
 * mantém o SCSS como fonte única e faz o dark mode do tema (`theme_color`) funcionar
 * sem código nenhum aqui.
 *
 * Os fallbacks abaixo só entram em cena se o CSS não tiver carregado (o `lateCss` do
 * Interactive pode chegar depois do JS): garantem um mapa legível em vez de um mapa
 * transparente.
 */

export interface MapPalette {
    /** Uma cor por banda, na mesma ordem de `thresholds` (ascendente). */
    bands: string[];
    bandOpacity: number;
    totality: string;
    totalityLine: string;
    land: string;
    /** Silhueta do mundo (contexto, fora da Ibéria) — sempre mais discreta que
     *  `land`, para a Ibéria continuar a ler-se como o foco da peça. */
    worldLand: string;
    /** Linha do limite de visibilidade (pôr-do-sol) — distinta de `coastline`. */
    visibilityLine: string;
    coastline: string;
    distritoLine: string;
    concelhoHover: string;
    concelhoSelected: string;
    sede: string;
    /** Cidades espanholas: distintas das sedes portuguesas, que são seleccionáveis. */
    city: string;
    label: string;
}

const FALLBACK: MapPalette = {
    bands: ['#f0d9a0', '#e6bf7f', '#d8a163', '#c4804d', '#a9603f', '#8a4438', '#672f33', '#45202f'],
    bandOpacity: 0.62,
    totality: '#0b0416',
    totalityLine: '#f5e9c6',
    land: '#2a2340',
    worldLand: '#241f36',
    visibilityLine: '#8f86a8',
    coastline: '#8f86a8',
    distritoLine: '#6b6386',
    concelhoHover: '#f5e9c6',
    concelhoSelected: '#ffffff',
    sede: '#f5e9c6',
    city: '#cbbfe3',
    label: '#f5e9c6',
};

/**
 * `bandCount` vem do próprio `map-geo.json` (`thresholds.length`), nunca de uma
 * constante aqui — se o gerador mudar o número de quebras, a rampa acompanha e o
 * fallback repete-se em vez de deixar bandas sem cor.
 */
export function readPalette(el: HTMLElement, bandCount: number): MapPalette {
    const cs = getComputedStyle(el);
    const value = (name: string, fallback: string): string => {
        const v = cs.getPropertyValue(name).trim();
        return v || fallback;
    };

    const bands: string[] = [];
    for (let i = 0; i < bandCount; i++) {
        bands.push(value(`--eclipse-band-${i}`, FALLBACK.bands[i] ?? FALLBACK.bands[FALLBACK.bands.length - 1]));
    }

    const opacity = parseFloat(value('--eclipse-band-opacity', String(FALLBACK.bandOpacity)));

    return {
        bands,
        bandOpacity: Number.isFinite(opacity) ? opacity : FALLBACK.bandOpacity,
        totality: value('--eclipse-totality', FALLBACK.totality),
        totalityLine: value('--eclipse-totality-line', FALLBACK.totalityLine),
        land: value('--eclipse-land', FALLBACK.land),
        worldLand: value('--eclipse-world-land', FALLBACK.worldLand),
        visibilityLine: value('--eclipse-visibility-line', FALLBACK.visibilityLine),
        coastline: value('--eclipse-coastline', FALLBACK.coastline),
        distritoLine: value('--eclipse-distrito-line', FALLBACK.distritoLine),
        concelhoHover: value('--eclipse-concelho-hover', FALLBACK.concelhoHover),
        concelhoSelected: value('--eclipse-concelho-selected', FALLBACK.concelhoSelected),
        sede: value('--eclipse-sede', FALLBACK.sede),
        city: value('--eclipse-city', FALLBACK.city),
        label: value('--eclipse-label', FALLBACK.label),
    };
}
